import React from "react";

/* Feature icons, drawn in the mark's own language: a 24-unit grid, hairline
   strokes at 1.3, blocks grounded on a baseline, and exactly ONE amber element
   each — the lit thing. Nothing here is an off-the-shelf icon set; the
   engagements repo has 35 amenity icons (pool, gym, clubhouse) and none of
   them describe a platform capability.

   Rule: if an icon needs more than six strokes to read, the idea is wrong. */

/* The icon accent is BRASS, not amber. Amber is reserved for things that
   are literally lit — windows, the beacon, a live session dot. */
const A = "#C3AE7C";

type Props = { name: string; size?: number };

export default function Icon({ name, size = 28 }: Props) {
  const p = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.3,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };

  const shapes: Record<string, React.ReactNode> = {
    /* dwell, space by space */
    bars: (
      <>
        <path d="M3 21h18" {...p} />
        <path d="M6 21v-5M10.5 21v-9M19 21v-7" {...p} />
        <rect x="13" y="6" width="3" height="15" fill={A} />
      </>
    ),
    /* a link, and who sent it */
    share: (
      <>
        <path d="M9 14.5 15 9" {...p} />
        <path d="M13.5 6.5a3.2 3.2 0 1 1 4.5 4.5l-2 2" {...p} />
        <path d="M10.5 17.5a3.2 3.2 0 1 1-4.5-4.5l2-2" {...p} />
        <circle cx="19" cy="5" r="2.2" fill={A} />
      </>
    ),
    /* the walkthrough, inside someone else's page */
    embed: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="1.6" {...p} />
        <path d="M3 9h18" {...p} />
        <rect x="6" y="12" width="7" height="4.5" fill={A} />
      </>
    ),
    /* the lead, arriving where the team already works */
    crm: (
      <>
        <path d="M4 20v-1.5a4 4 0 0 1 4-4h3" {...p} />
        <circle cx="9.5" cy="8" r="3.2" {...p} />
        <path d="M15 17.5h5M17.5 15v5" stroke={A} strokeWidth={1.6} strokeLinecap="round" fill="none" />
      </>
    ),
    /* two people, one tour */
    guided: (
      <>
        <rect x="2.5" y="5" width="13" height="9.5" rx="1.4" {...p} />
        <path d="M6 18.5h6" {...p} />
        <path d="M9 14.5v4" {...p} />
        <circle cx="19" cy="9" r="2.6" fill={A} />
      </>
    ),
    /* what is sold, what is left */
    inventory: (
      <>
        <rect x="3.5" y="4" width="7" height="7" rx="1" {...p} />
        <rect x="13.5" y="13" width="7" height="7" rx="1" {...p} />
        <rect x="3.5" y="13" width="7" height="7" rx="1" {...p} />
        <rect x="13.5" y="4" width="7" height="7" rx="1" fill={A} stroke="none" />
      </>
    ),
    /* one unit, for the buyer down to one */
    focus: (
      <>
        <path d="M4 10.5 12 4l8 6.5" {...p} />
        <path d="M6 10v10h12V10" {...p} />
        <rect x="10" y="13.5" width="4" height="6.5" fill={A} />
      </>
    ),
    /* where they are opening from */
    map: (
      <>
        <path d="M3 6.5 9 4.5l6 2 6-2v13l-6 2-6-2-6 2z" {...p} />
        <path d="M9 4.5v15M15 6.5v15" {...p} />
        <circle cx="12" cy="11" r="2.4" fill={A} />
      </>
    ),
    /* project, tower, unit */
    tiers: (
      <>
        <path d="M3.5 18.5h17" {...p} />
        <rect x="4.5" y="13" width="5" height="5.5" {...p} />
        <rect x="15" y="9" width="5" height="9.5" {...p} />
        <rect x="10" y="4.5" width="4" height="14" fill={A} stroke="none" />
      </>
    ),
    /* works when the signal does not */
    offline: (
      <>
        <path d="M5 11.5a9.5 9.5 0 0 1 14 0" {...p} />
        <path d="M8 14.5a5.4 5.4 0 0 1 8 0" {...p} />
        <circle cx="12" cy="18" r="1.6" fill={A} />
        <path d="M4 4l16 16" stroke="currentColor" strokeWidth={1.3} strokeLinecap="round" opacity={0.45} fill="none" />
      </>
    ),
    /* a presenter at two in the morning */
    ai: (
      <>
        <rect x="4" y="7" width="16" height="11" rx="2.4" {...p} />
        <path d="M12 7V3.5" {...p} />
        <circle cx="8.6" cy="12.5" r="1.5" fill={A} />
        <circle cx="15.4" cy="12.5" r="1.5" fill="currentColor" opacity={0.5} />
      </>
    ),
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      style={{ display: "block", flex: "none" }}
    >
      {shapes[name] ?? shapes.bars}
    </svg>
  );
}
